import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import {
  featuresApi,
  type FeatureFilters,
  type CreateFeatureInput,
  type UpdateFeatureInput,
} from '@/lib/api/features';
import { issueKeys } from './use-issues';

export const featureKeys = {
  all: ['features'] as const,
  lists: () => [...featureKeys.all, 'list'] as const,
  list: (filters: FeatureFilters) => [...featureKeys.lists(), filters] as const,
  details: () => [...featureKeys.all, 'detail'] as const,
  detail: (id: string) => [...featureKeys.details(), id] as const,
  compute: (id: string) => [...featureKeys.detail(id), 'compute'] as const,
};

export function useFeatures(filters: FeatureFilters = {}) {
  return useInfiniteQuery({
    queryKey: featureKeys.list(filters),
    queryFn: ({ pageParam }) =>
      featuresApi.list({ ...filters, cursor: pageParam }),
    getNextPageParam: (lastPage) =>
      lastPage.meta?.hasMore && lastPage.meta.cursor ? lastPage.meta.cursor : undefined,
    initialPageParam: undefined as string | undefined,
  });
}

export function useFeature(id: string) {
  return useQuery({
    queryKey: featureKeys.detail(id),
    queryFn: () => featuresApi.get(id),
    enabled: !!id,
    select: (response) => response.data,
  });
}

export function useCreateFeature() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateFeatureInput) => featuresApi.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: featureKeys.lists() });
      queryClient.invalidateQueries({ queryKey: issueKeys.all });
    },
  });
}

export function useUpdateFeature() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateFeatureInput) => featuresApi.update(input),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: featureKeys.lists() });
      queryClient.invalidateQueries({ queryKey: issueKeys.all });
      queryClient.setQueryData(featureKeys.detail(response.data.id), response);
    },
  });
}

export function useDeleteFeature() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => featuresApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: featureKeys.lists() });
      queryClient.invalidateQueries({ queryKey: issueKeys.all });
    },
  });
}

/**
 * Compute for one item, as two separate figure sets. The response carries no
 * combined figure and none must be derived from it.
 */
export function useFeatureCompute(id: string) {
  return useQuery({
    queryKey: featureKeys.compute(id),
    queryFn: () => featuresApi.getCompute(id),
    enabled: !!id,
  });
}
